import React, { useMemo } from 'react';

function HarvestAnalytics({ agents }) {
  const dataByEmployee = useMemo(() => {
    const grouped = agents.reduce((acc, a) => {
      const key = a.employee_name || 'Unknown';
      if (!acc[key]) acc[key] = { label: key, heads: 0, kilos: 0 };
      acc[key].heads += a.heads;
      acc[key].kilos += a.kilos;
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => b.heads - a.heads);
  }, [agents]);

  const dataByLocation = useMemo(() => {
    const grouped = agents.reduce((acc, a) => {
      const key = a.location_name || 'Unknown';
      if (!acc[key]) acc[key] = { label: key, heads: 0, kilos: 0 };
      acc[key].heads += a.heads;
      acc[key].kilos += a.kilos;
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => b.heads - a.heads);
  }, [agents]);

  const stats = useMemo(() => {
    const heads = agents.reduce((acc, a) => acc + a.heads, 0);
    const kilos = agents.reduce((acc, a) => acc + a.kilos, 0);
    return { heads, kilos };
  }, [agents]);

  const avgHeadsPerRecord = agents.length > 0 ? (stats.heads / agents.length).toFixed(2) : 0;
  const avgWeightPerHead = stats.heads > 0 ? (stats.kilos / stats.heads).toFixed(2) : 0;

  const maxEmpHeads = Math.max(...dataByEmployee.map(d => d.heads), 1);
  const maxLocHeads = Math.max(...dataByLocation.map(d => d.heads), 1);

  return (
    <div className="form-section" style={{marginBottom: '2rem'}}>
      <h3 style={{marginBottom: '1.5rem'}}>Harvest Analytics</h3>
      
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2rem'}}>
        <div style={{padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center', border: '1px solid #eee'}}>
          <div style={{fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem'}}>Total Heads Harvested</div>
          <div style={{fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--secondary-color)'}}>{stats.heads.toLocaleString()}</div>
        </div>
        <div style={{padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center', border: '1px solid #eee'}}>
          <div style={{fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem'}}>Total Weight</div>
          <div style={{fontSize: '1.2rem', fontWeight: 'bold', color: '#2c3e50'}}>{stats.kilos.toLocaleString()} kg</div>
        </div>
        <div style={{padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center', border: '1px solid #eee'}}>
          <div style={{fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem'}}>Avg Weight / Head</div>
          <div style={{fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--secondary-color)'}}>{avgWeightPerHead} kg</div>
        </div>
        <div style={{padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center', border: '1px solid #eee'}}>
          <div style={{fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem'}}>Avg Heads / Agent</div>
          <div style={{fontSize: '1.2rem', fontWeight: 'bold', color: '#2c3e50'}}>{avgHeadsPerRecord}</div>
        </div>
      </div>

      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem'}}>
        <div>
          <h4>Top Employees (by Heads)</h4>
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
            {dataByEmployee.map((d, i) => (
              <div key={i}>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px'}}>
                  <span>{d.label} <small style={{color: '#666'}}>({(d.kilos / d.heads).toFixed(2)} kg/head)</small></span>
                  <strong>{d.heads} heads</strong>
                </div>
                <div style={{height: '12px', backgroundColor: '#eee', borderRadius: '6px', overflow: 'hidden'}}>
                  <div style={{height: '100%', width: `${(d.heads / maxEmpHeads) * 100}%`, backgroundColor: '#3498db'}} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4>Harvests by Location</h4>
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
            {dataByLocation.map((d, i) => (
              <div key={i}>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px'}}>
                  <span>{d.label} <small style={{color: '#666'}}>({(d.kilos / d.heads).toFixed(2)} kg/head)</small></span>
                  <strong>{d.heads} heads</strong>
                </div>
                <div style={{height: '12px', backgroundColor: '#eee', borderRadius: '6px', overflow: 'hidden'}}>
                  <div style={{height: '100%', width: `${(d.heads / maxLocHeads) * 100}%`, backgroundColor: 'var(--secondary-color)'}} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HarvestAnalytics;
